import {type ABMonitorRowItem} from "../models";
import * as ApiHelper from "../../api/ApiHelper";
import {getDate} from "../../api/ApiHelper";

import {type ProductDataDTO} from "../../dto/ProductDataDTO";
import {transformProduct} from "../../ProductDetails/transformers/ProductDataTransformer";
import {constructUrl, resolveApiUrl} from "../../utils/url/urlResolver";
import {getNextUID} from "../../utils/uid";
import {type PriceDataDTO} from "../../dto/PriceDataDTO";
import {type NetPriceData} from "../../models/PriceData";
import {getTotalPrice} from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import dayjs from "dayjs";
import {apiPOST} from "../../common/api/apiCallsService";
import {
  type PaginatedFetchFuncProps,
  type PaginatedFetchFuncResponse,
} from "../../OrdersQuotes/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import {type GroupedAccountsData} from "../../OrdersQuotes/common/DownloadOrderHistoryService/types";
import {type SDAccountDataDTO} from "../../OrdersQuotes/OrderListPage/api/fetchOrderHistory/dto";
import {transformOrderAccount} from "../../OrdersQuotes/OrderListPage/api/fetchOrderHistory/fetchOrderHistory";

interface ResponseItemDTO {
  ocsalesDocumentDataList: OrderConfirmationItemDTO[];
  nextSkipToken: string | undefined;
  pageCount: number;
  totalCount: number;
}

interface OrderConfirmationItemDTO {
  salesDocument: string;
  customerOrderId: string;
  products: ProductItemDTO[];
  account?: SDAccountDataDTO;
}

interface ProductItemDTO {
  product: ProductDataDTO;
  lineNumber: number;
  scheduledLineNumber: number | null;
  packSize: number;
  packCode: string;
  currentConfirmedDeliveryDate: string; // YYYY-MM-DD
  previousConfirmedDeliveryDate: string; // YYYY-MM-DD
  dateRequested: string; // YYYY-MM-DD
  currentConfirmedQuantity: number;
  previousConfirmedQuantity: number;
  requestedQuantity: number;
  stockReserved: number;
  status: string;
  primaryWarehouseName: string;
  netPrice: PriceDataDTO;
  priceUnit: string;
}

const baseUrl = "/abmonitor/list";

export interface ABMonitorDataRequest {
  dateFrom?: Date;
  dateTo?: Date;
  account?: GroupedAccountsData;
}

export interface ABMonitorDataResponse {
  abMonitorData: ABMonitorRowItem[];
  totalResults: number;
  pageCount: number;
}

export interface FetchABMonitorDataProps extends PaginatedFetchFuncProps<ABMonitorDataRequest> {}

export interface FetchABMonitorDataResponse extends PaginatedFetchFuncResponse<ABMonitorDataResponse> {}

export async function fetchABMonitorData({
  data,
  skipToken,
  signal,
}: FetchABMonitorDataProps): Promise<FetchABMonitorDataResponse> {
  const { dateFrom, dateTo, account } = data;
  const params = new URLSearchParams();
  if (typeof skipToken !== "undefined") {
    params.append("skiptoken", skipToken);
  }
  if (dateFrom) {
    params.append("startDate", dayjs(dateFrom).format("YYYY-MM-DD"));
  }
  if (dateTo) {
    params.append("endDate", dayjs(dateTo).format("YYYY-MM-DD"));
  }

  const apiUrl = resolveApiUrl(constructUrl(baseUrl, params));

  const response = await apiPOST(apiUrl, {
    body: account ? JSON.stringify(account) : undefined,
    headers: {
      "content-type": "application/json",
      CSRFToken: ApiHelper.getCsrfValue(),
    },
    signal,
  });

  const dto: ResponseItemDTO = await response.json();

  return {
    data: {
      abMonitorData: dto.ocsalesDocumentDataList.map(transformLineItem).flat(),
      totalResults: dto.totalCount,
      pageCount: dto.pageCount,
    },
    nextSkipToken: dto.nextSkipToken,
  };
}

function transformLineItem(dto: OrderConfirmationItemDTO): ABMonitorRowItem[] {
  let products: ProductItemDTO[] = dto.products || [];

  products = products.sort((a, b) => (a.lineNumber && b.lineNumber ? a.lineNumber - b.lineNumber : 0));
  if (products.length === 1) {
    products[0].scheduledLineNumber = null;
  } else if (products.length > 0) {
    products.forEach((product, index) => {
      const nextProduct = products[index + 1];
      const prevProduct = products[index - 1];
      if (
        (nextProduct && product.lineNumber !== nextProduct.lineNumber && index === 0) ||
        (nextProduct &&
          product.lineNumber !== nextProduct.lineNumber &&
          index > 0 &&
          prevProduct &&
          product.lineNumber !== prevProduct.lineNumber) ||
        (prevProduct && product.lineNumber !== prevProduct.lineNumber && index === products.length - 1)
      ) {
        product.scheduledLineNumber = null;
      }
    });
  }

  return products.map((lineItemDto) => {
    const product = transformProduct(lineItemDto.product);
    const baseValue = lineItemDto.netPrice?.value ?? 0;
    const price: NetPriceData = {
      value: baseValue,
      currencyIso: lineItemDto.netPrice?.currencyIso ?? "XXX",
      unit: +(lineItemDto.priceUnit ?? 1),
      isSubscription: !product.quantityVisible,
      // TODO - AB Monitor does not supply surcharge data
      surcharge: 0,
      baseValue,
    };

    return {
      id: getNextUID(),
      orderLineNo:
        (lineItemDto.lineNumber && lineItemDto.scheduledLineNumber
          ? lineItemDto.lineNumber + ":" + lineItemDto.scheduledLineNumber
          : lineItemDto.lineNumber
          ? lineItemDto.lineNumber
          : "-") + "",
      customerOrderNo: dto.customerOrderId,
      schaefflerOrderNo: dto.salesDocument,
      product,
      isNew: lineItemDto.status === "1",
      isUpdated: lineItemDto.status === "2",
      curQuantity: lineItemDto.currentConfirmedQuantity,
      prevQuantity: lineItemDto.previousConfirmedQuantity,
      stockReserved: lineItemDto.stockReserved,
      totalQuantity: lineItemDto.requestedQuantity,
      curDeliveryDate: getDate(lineItemDto.currentConfirmedDeliveryDate),
      prevDeliveryDate: getDate(lineItemDto.previousConfirmedDeliveryDate),
      requestedDeliveryDate: getDate(lineItemDto.dateRequested),

      packCode: lineItemDto.packCode,
      packSize: lineItemDto.packSize,
      primaryWarehouseName: lineItemDto.primaryWarehouseName,
      netPrice: price,
      totalPrice: getTotalPrice(price, lineItemDto.currentConfirmedQuantity),
      account: transformOrderAccount(dto.account),
    };
  });
}
